import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, CheckCircle2, ChevronDown, Link2 } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { borderPrimary, primaryText, secondaryText, selectableName, semiBoldText, smallTextSize, tertiaryText } from '../../../styling/stylingHelper';
import { MultiSelectFilter } from '../../ui/MultiSelectFilter';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../../ui/InventorySlideOver';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import {
  linkProductsDryRun,
  linkProductsCommit,
  LinkTargetInput,
  LinkPlanRow,
  LinkDryRunSummary,
  LinkResolution,
  LinkCommitSummary,
} from '../../../services/inventory/inventoryService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  /** The "Not linked" rows currently on screen, already mapped by the caller. */
  targets: LinkTargetInput[];
  /** Fired after a commit that changed something, so the page can reload its data. */
  onCommitted: () => void;
}

// 'intro' states what will be analysed (the AI pass costs money, so it is one deliberate
// click); 'review' is the same shape as the Excel importer's review step; 'done' reports
// what actually landed, including per-row failures.
type Step = 'intro' | 'review' | 'done';

/**
 * "Link products" automation: bulk-links the not-linked inventory rows to order-history
 * products. Mirrors the Excel importer's analyze → review → commit flow, minus the upload
 * (the rows come from the page) and minus the create-custom option (the whole point is to
 * land on real purchase data).
 */
export default function LinkProductsModal({ isOpen, onClose, restaurantId, targets, onCommitted }: Props) {
  const { t } = useTranslation();
  const { availableProducts, loadAvailableProducts } = useInventoryStore();

  const [step, setStep] = useState<Step>('intro');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [rows, setRows] = useState<LinkPlanRow[]>([]);
  const [summary, setSummary] = useState<LinkDryRunSummary | null>(null);
  const [resolutions, setResolutions] = useState<Record<number, LinkResolution>>({});
  const [commitSummary, setCommitSummary] = useState<LinkCommitSummary | null>(null);

  const [searchOpenFor, setSearchOpenFor] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSupplier, setSearchSupplier] = useState<string[]>([]);
  const [searchCategory, setSearchCategory] = useState<string[]>([]);
  const [searchFilterOpen, setSearchFilterOpen] = useState<'supplier' | 'category' | null>(null);

  // Fresh state on every open — a previous run's plan is stale the moment links change.
  useEffect(() => {
    if (!isOpen) return;
    setStep('intro');
    setBusy(false);
    setError(null);
    setRows([]);
    setSummary(null);
    setResolutions({});
    setCommitSummary(null);
    setSearchOpenFor(null);
    setSearchTerm('');
    setSearchSupplier([]);
    setSearchCategory([]);
    setSearchFilterOpen(null);
    loadAvailableProducts(restaurantId);
  }, [isOpen, restaurantId, loadAvailableProducts]);

  // Confident matches are pre-selected; everything else defaults to skip, so an
  // untouched review never links a product the AI was unsure about.
  const initResolutions = (planRows: LinkPlanRow[]): Record<number, LinkResolution> => {
    const out: Record<number, LinkResolution> = {};
    for (const r of planRows) {
      out[r.rowIndex] = r.match.status === 'auto' && r.match.productKey
        ? { action: 'bind', productKey: r.match.productKey }
        : { action: 'skip' };
    }
    return out;
  };

  const runDryRun = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await linkProductsDryRun(restaurantId, targets);
      setRows(res.products);
      setSummary(res.summary);
      setResolutions(initResolutions(res.products));
      setStep('review');
    } catch (err) {
      console.error('Link products dry-run failed:', err);
      setError(t('inventory.link.errorAnalyze'));
    } finally {
      setBusy(false);
    }
  };

  const bound = useMemo(
    () => rows
      .map((r) => ({ row: r, res: resolutions[r.rowIndex] }))
      .filter((x): x is { row: LinkPlanRow; res: { action: 'bind'; productKey: string } } => x.res?.action === 'bind'),
    [rows, resolutions]
  );

  // A deduped row stands for the same product in several sections. The match and the review
  // happen ONCE per product; the commit still has to touch every section, because a re-link is
  // per assignment (M40). Keyed by rowIndex, which the plan rows carry back from the dry-run.
  const additionalByRowIndex = useMemo(() => {
    const m = new Map<number, NonNullable<LinkTargetInput['additionalTargets']>>();
    for (const target of targets) {
      if (target.additionalTargets?.length) m.set(target.rowIndex, target.additionalTargets);
    }
    return m;
  }, [targets]);

  const runCommit = async () => {
    if (bound.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      // rowIndex only tags the per-row result in the server's log/summary, and the server
      // dedups on (sectionId, oldProductKey) anyway — so a running counter is enough to keep
      // the fanned-out entries distinct.
      let nextIndex = 0;
      const res = await linkProductsCommit(
        restaurantId,
        bound.flatMap(({ row, res }) => [
          {
            rowIndex: row.rowIndex,
            sectionId: row.sectionId,
            oldProductKey: row.productKey,
            productKey: res.productKey,
            inventoryCategoryId: row.inventoryCategoryId,
          },
          ...(additionalByRowIndex.get(row.rowIndex) ?? []).map((extra) => ({
            rowIndex: rows.length + (nextIndex++),
            sectionId: extra.sectionId,
            oldProductKey: extra.productKey,
            productKey: res.productKey,
            inventoryCategoryId: extra.inventoryCategoryId,
          })),
        ])
      );
      setCommitSummary(res.summary);
      setStep('done');
      if (res.summary.linked > 0) onCommitted();
    } catch (err) {
      console.error('Link products commit failed:', err);
      setError(t('inventory.link.errorCommit'));
    } finally {
      setBusy(false);
    }
  };

  const setResolution = (rowIndex: number, r: LinkResolution) =>
    setResolutions((prev) => ({ ...prev, [rowIndex]: r }));

  const toggleSearchFor = (rowIndex: number) => {
    setSearchOpenFor((prev) => (prev === rowIndex ? null : rowIndex));
    setSearchTerm('');
    setSearchSupplier([]);
    setSearchCategory([]);
    setSearchFilterOpen(null);
  };

  // productKey -> display metadata, merged from the order-history picker and the plan's
  // candidate shortlists (a suggestion can reference a product the picker paginated away).
  const metaByKey = useMemo(() => {
    const m = new Map<string, { name: string; supplier?: string; lastOrderDate?: string; last60Qty?: number; last60Unit?: string; unitPrice?: number; unitPriceUnit?: string; productPrice?: number }>();
    for (const a of availableProducts) {
      m.set(a.productKey, { name: a.productName, supplier: a.platform, lastOrderDate: a.latestPurchaseDate, last60Qty: a.last60Qty, last60Unit: a.last60Unit, unitPrice: a.unitPrice, unitPriceUnit: a.unitPriceUnit, productPrice: a.productPrice });
    }
    for (const r of rows) {
      for (const s of r.match.suggestions ?? []) {
        if (!m.has(s.productKey)) m.set(s.productKey, { name: s.productName, supplier: s.supplier, lastOrderDate: s.lastOrderDate, last60Qty: s.last60Qty, last60Unit: s.last60Unit, unitPrice: s.unitPrice, unitPriceUnit: s.unitPriceUnit, productPrice: s.productPrice });
      }
    }
    return m;
  }, [availableProducts, rows]);

  const fmtMeta = (key: string): string => {
    const meta = metaByKey.get(key);
    if (!meta) return '';
    const bits: string[] = [];
    if (meta.supplier) bits.push(meta.supplier);
    if (typeof meta.productPrice === 'number' && meta.productPrice > 0) {
      bits.push(t('inventory.import.buyPrice', { value: meta.productPrice.toFixed(2) }));
    }
    if (typeof meta.unitPrice === 'number') {
      bits.push(t('inventory.import.unitPrice', { value: meta.unitPrice.toFixed(2), unit: meta.unitPriceUnit ?? 'pcs' }));
    }
    if (meta.lastOrderDate) bits.push(t('inventory.import.lastOrdered', { date: new Date(meta.lastOrderDate).toLocaleDateString() }));
    if (typeof meta.last60Qty === 'number') {
      const v = Math.round(meta.last60Qty * 10) / 10;
      bits.push(t('inventory.import.qty60', { value: v, unit: meta.last60Unit ?? 'pcs' }));
    }
    return bits.join(' · ');
  };

  const supplierOptions = useMemo(
    () => Array.from(new Set(availableProducts.map((a) => a.platform).filter(Boolean))).sort(),
    [availableProducts]
  );

  const categoryOptions = useMemo(
    () => Array.from(new Set(availableProducts.map((a) => a.category).filter((c): c is string => !!c))).sort(),
    [availableProducts]
  );

  const filteredAvailable = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return availableProducts.filter((a) => {
      if (term && !a.productName.toLowerCase().includes(term)) return false;
      if (searchSupplier.length > 0 && !searchSupplier.includes(a.platform)) return false;
      if (searchCategory.length > 0 && (!a.category || !searchCategory.includes(a.category))) return false;
      return true;
    }).slice(0, 20);
  }, [availableProducts, searchTerm, searchSupplier, searchCategory]);

  // Progress counts every row the AI did not auto-bind — those are the decisions the
  // user actually has to make.
  const reviewTotal = rows.filter((r) => r.match.status !== 'auto').length;
  const reviewDone = rows.filter((r) => r.match.status !== 'auto' && resolutions[r.rowIndex]?.action === 'bind').length;
  const reviewPct = reviewTotal > 0 ? Math.round((reviewDone / reviewTotal) * 100) : 100;

  // The not-linked row's own frozen price (per std unit) lives on the frozen `targets`
  // prop, keyed by rowIndex — the dry-run response doesn't echo it back.
  const priceByRow = useMemo(() => {
    const m = new Map<number, { value: number; unit: string }>();
    for (const t of targets) {
      if (typeof t.price === 'number' && t.price > 0) m.set(t.rowIndex, { value: t.price, unit: t.priceUnit ?? 'pcs' });
    }
    return m;
  }, [targets]);

  const rowSubtitle = (r: LinkPlanRow): string => {
    const bits: string[] = [];
    if (r.sectionName) bits.push(r.sectionName);
    if (r.inventoryCategoryName) bits.push(r.inventoryCategoryName);
    if (typeof r.count === 'number') bits.push(`${r.count} ${r.countUnit ?? ''}`.trim());
    return bits.join(' · ');
  };

  const getFooter = () => {
    if (step === 'intro') {
      return (
        <>
          <button type="button" onClick={onClose} className={slideOverCancelClass}>
            {t('inventory.cancel')}
          </button>
          <button
            type="button"
            onClick={runDryRun}
            disabled={busy || targets.length === 0}
            className={slideOverPrimaryClass}
          >
            {busy ? t('inventory.link.analyzing') : t('inventory.link.analyze')}
          </button>
        </>
      );
    }
    if (step === 'review') {
      return (
        <>
          <button type="button" onClick={() => setStep('intro')} disabled={busy} className={slideOverCancelClass}>
            {t('inventory.import.back')}
          </button>
          <button
            type="button"
            onClick={runCommit}
            disabled={busy || bound.length === 0}
            className={slideOverPrimaryClass}
          >
            {busy ? t('inventory.link.linking') : t('inventory.link.linkCount', { n: bound.length })}
          </button>
        </>
      );
    }
    return (
      <button type="button" onClick={onClose} className={slideOverPrimaryClass}>
        {t('inventory.link.close')}
      </button>
    );
  };

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={t('inventory.link.title')}
      description={t('inventory.link.description')}
      footer={getFooter()}
      closeLabel={t('inventory.cancel') as string}
    >
      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {step === 'intro' && (
        <div className={cn('p-4 rounded-xl border', borderPrimary)}>
          <p className={cn(smallTextSize, primaryText, semiBoldText)}>
            {t('inventory.link.introCount', { n: targets.length })}
          </p>
          <p className={cn('mt-2 text-[13px] leading-5', secondaryText)}>{t('inventory.link.introBody')}</p>
        </div>
      )}

      {step === 'review' && summary && (
        <div>
          {/* Summary chips */}
          <div className="flex flex-wrap gap-2 mb-4">
            {[
              { label: t('inventory.link.summaryTotal'), value: summary.total },
              { label: t('inventory.import.summaryAutoMatched'), value: summary.autoMatched },
              { label: t('inventory.import.summaryToReview'), value: summary.toReview },
              { label: t('inventory.import.summaryUnmatched'), value: summary.unmatched },
            ].map((chip, i) => (
              <span key={i} className={cn(smallTextSize, 'px-3 py-1.5 rounded-lg border', borderPrimary, secondaryText)}>
                {chip.label}: <span className={cn(semiBoldText, primaryText)}>{chip.value}</span>
              </span>
            ))}
          </div>

          {summary.availableProducts === 0 && (
            <p className={cn(smallTextSize, tertiaryText, 'mb-4')}>{t('inventory.link.noAvailableProducts')}</p>
          )}

          {/* Review progress */}
          {reviewTotal > 0 && (
            <div className="sticky top-0 z-10 -mx-6 px-6 py-3 mb-4 bg-card dark:bg-card-dark border-b border-zinc-200 dark:border-white/20">
              <div className="flex items-center justify-between mb-1.5">
                <span className={cn(smallTextSize, semiBoldText, primaryText)}>
                  {t('inventory.import.reviewProgress', { done: reviewDone, total: reviewTotal })}
                </span>
                <span className={cn('text-[11px]', tertiaryText)}>
                  {reviewDone === reviewTotal ? t('inventory.import.reviewAllDone') : `${reviewPct}%`}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
                <div className="h-full rounded-full bg-darkGreenColor dark:bg-darkGreenColor-dark transition-all duration-300" style={{ width: `${reviewPct}%` }} />
              </div>
            </div>
          )}

          <div className="space-y-2">
            {rows.map((r) => {
              const res = resolutions[r.rowIndex] ?? { action: 'skip' as const };
              const boundKey = res.action === 'bind' ? res.productKey : null;
              const candidates = (r.match.suggestions ?? []).filter((s) => s.productKey !== boundKey);
              return (
                <div key={r.rowIndex} className={cn('p-3 rounded-xl border', borderPrimary)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={cn(smallTextSize, semiBoldText, primaryText, selectableName)}>{r.productName}</p>
                      <p className={cn('text-[11px]', tertiaryText)}>
                        {rowSubtitle(r)}
                        {r.match.method === 'ai' && typeof r.match.confidence === 'number' && (
                          <span className="ml-1 text-amber-600 dark:text-amber-400">
                            · {t('inventory.import.aiConfidence', { pct: Math.round(r.match.confidence * 100) })}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {priceByRow.get(r.rowIndex) && (
                        <span className={cn('text-[11px] px-2 py-1 rounded-lg border font-semibold', borderPrimary, primaryText, 'bg-amber-50 dark:bg-amber-900/20')}>
                          {t('inventory.link.rowPrice', {
                            value: priceByRow.get(r.rowIndex)!.value.toFixed(2),
                            unit: priceByRow.get(r.rowIndex)!.unit,
                          })}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleSearchFor(r.rowIndex)}
                        className={cn('text-[11px] px-2 py-1 rounded-lg border flex items-center gap-1', borderPrimary, searchOpenFor === r.rowIndex ? 'bg-darkGreenColor/10 dark:bg-darkGreenColor-dark/10 text-darkGreenColor dark:text-darkGreenColor-dark' : secondaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800')}
                      >
                        <Search className="w-3 h-3" /> {t('inventory.import.bind')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setResolution(r.rowIndex, { action: 'skip' })}
                        className={cn('text-[11px] px-2 py-1 rounded-lg border', borderPrimary, res.action === 'skip' ? 'bg-gray-100 dark:bg-gray-700' : secondaryText, 'hover:bg-gray-50 dark:hover:bg-gray-800')}
                      >
                        {t('inventory.import.skip')}
                      </button>
                    </div>
                  </div>

                  {/* The single product this row will be linked to. */}
                  {boundKey && (
                    <div className="mt-2 flex items-center justify-between gap-2 p-1.5 pl-2 rounded-lg bg-green-50 dark:bg-green-900/20">
                      <div className="min-w-0">
                        <p className="text-[11px] text-green-700 dark:text-green-300 flex items-start gap-1">
                          <CheckCircle2 className="w-3 h-3 shrink-0 mt-px" />
                          <span className={selectableName}>{metaByKey.get(boundKey)?.name ?? r.match.matchedName ?? boundKey}</span>
                        </p>
                        {fmtMeta(boundKey) && <p className="text-[10px] text-green-600/80 dark:text-green-400/70 truncate pl-4">{fmtMeta(boundKey)}</p>}
                      </div>
                      <button
                        type="button"
                        onClick={() => setResolution(r.rowIndex, { action: 'skip' })}
                        className="p-1 rounded hover:bg-green-100 dark:hover:bg-green-800/40 shrink-0"
                        aria-label={t('inventory.import.skip') as string}
                      >
                        <X className="w-3 h-3 text-green-700 dark:text-green-300" />
                      </button>
                    </div>
                  )}

                  {/* Candidate quick-picks (AI-pruned). Clicking one replaces the bind. */}
                  {candidates.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {candidates.map((s) => (
                        <button
                          key={s.productKey}
                          type="button"
                          onClick={() => setResolution(r.rowIndex, { action: 'bind', productKey: s.productKey })}
                          className={cn('block w-full text-left px-2 py-1 rounded-lg border', borderPrimary, 'hover:bg-blue-50 dark:hover:bg-blue-900/20')}
                        >
                          <span className="text-[11px] text-blue-600 dark:text-blue-400">+ {s.productName}</span>
                          {fmtMeta(s.productKey) && <span className={cn('block text-[10px]', tertiaryText)}>{fmtMeta(s.productKey)}</span>}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Order-history search — click a result to link this row to it. */}
                  {searchOpenFor === r.rowIndex && (
                    <div className="mt-3 p-3 rounded-xl border bg-gray-50/60 dark:bg-zinc-800/40 border-zinc-200 dark:border-zinc-700">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-textQuaternary-dark" />
                          <input
                            autoFocus
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={t('inventory.import.searchPlaceholder') as string}
                            className={cn('w-full pl-9 pr-3 py-2 rounded-xl border text-sm', borderPrimary, 'bg-white dark:bg-zinc-900 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-500')}
                          />
                        </div>
                        <div className="flex gap-2 shrink-0">
                          {([
                            { key: 'supplier' as const, label: t('inventory.filterPlatform'), count: searchSupplier.length },
                            { key: 'category' as const, label: t('inventory.filterCategory'), count: searchCategory.length },
                          ]).map((f) => (
                            <button
                              key={f.key}
                              type="button"
                              onClick={() => setSearchFilterOpen((o) => (o === f.key ? null : f.key))}
                              className={cn(
                                'px-3 py-2 rounded-xl border bg-white dark:bg-zinc-900',
                                borderPrimary, smallTextSize, semiBoldText, secondaryText,
                                'flex items-center gap-1.5 hover:bg-gray-50 dark:hover:bg-gray-800',
                                searchFilterOpen === f.key && 'ring-2 ring-green-500/40 dark:ring-green-500/40'
                              )}
                            >
                              <span>{f.label}</span>
                              {f.count > 0 && (
                                <span className="bg-darkGreenColor dark:bg-darkGreenColor-dark text-white dark:text-white text-xs rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center">
                                  {f.count}
                                </span>
                              )}
                              <ChevronDown className={cn('w-4 h-4 transition-transform', searchFilterOpen === f.key && 'rotate-180')} />
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Inline filter grid — in-flow so the modal scroll never clips it. */}
                      {searchFilterOpen && (
                        <div className={cn('mt-2 p-3 rounded-xl border bg-white dark:bg-zinc-900', borderPrimary)}>
                          {searchFilterOpen === 'supplier' ? (
                            <MultiSelectFilter
                              label={t('inventory.filterPlatform')}
                              selectedValues={searchSupplier}
                              onValuesChange={setSearchSupplier}
                              availableOptions={supplierOptions}
                              valueType="platform"
                              maxHeight="max-h-[140px]"
                            />
                          ) : (
                            <MultiSelectFilter
                              label={t('inventory.filterCategory')}
                              selectedValues={searchCategory}
                              onValuesChange={setSearchCategory}
                              availableOptions={categoryOptions}
                              valueType="category"
                              maxHeight="max-h-[140px]"
                            />
                          )}
                        </div>
                      )}

                      <div className="mt-2 max-h-44 overflow-y-auto">
                        {filteredAvailable.map((a) => (
                          <button
                            key={a.productKey}
                            type="button"
                            onClick={() => {
                              setResolution(r.rowIndex, { action: 'bind', productKey: a.productKey });
                              setSearchOpenFor(null);
                            }}
                            className="block w-full text-left px-3 py-2 mb-1 rounded-lg bg-white dark:bg-zinc-900 border border-transparent dark:border-transparent hover:border-darkGreenColor/50 dark:hover:border-darkGreenColor-dark/50 transition-colors"
                          >
                            <span className={cn('text-sm', secondaryText)}>{a.productName}</span>
                            {fmtMeta(a.productKey) && <span className={cn('block text-[10px]', tertiaryText)}>{fmtMeta(a.productKey)}</span>}
                          </button>
                        ))}
                        {filteredAvailable.length === 0 && (
                          <p className={cn(smallTextSize, tertiaryText, 'text-center py-4')}>{t('inventory.noAvailableProducts')}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {step === 'done' && commitSummary && (
        <div className={cn('p-4 rounded-xl border', borderPrimary)}>
          <p className={cn(smallTextSize, semiBoldText, primaryText, 'flex items-center gap-2')}>
            <Link2 className="w-4 h-4 text-darkGreenColor dark:text-darkGreenColor-dark" />
            {t('inventory.link.doneLinked', { n: commitSummary.linked, total: commitSummary.requested })}
          </p>
          <div className="mt-3 space-y-1">
            {[
              { label: t('inventory.link.doneAlreadyInSection'), value: commitSummary.alreadyInSection },
              { label: t('inventory.link.doneNotFound'), value: commitSummary.notFound + commitSummary.targetNotFound },
              { label: t('inventory.link.doneFailed'), value: commitSummary.failed },
            ].filter((x) => x.value > 0).map((x, i) => (
              <p key={i} className={cn('text-[12px]', secondaryText)}>{x.label}: <span className={semiBoldText}>{x.value}</span></p>
            ))}
          </div>
        </div>
      )}
    </InventorySlideOver>
  );
}
